interface HeliumEventGlobalInfo {
  userId: string;
  sessionId: string;
  brand: string;
  pagePath: string;
}
export type FeatureProviders = "helium" | "native" | "third-party";
export type ComponentTypes =
  | "tag_recommendations"
  | "curated_picks"
  | "similar"
  | "inline-similar"
  | "complementary"
  | "low_ticket"
  | "merchandize"
  | "faq"
  | "product_list"
  | "memory_recommendations"
  | "because_viewed"
  | "recently_viewed";
export type EventTypes = "page" | "ecommerce" | "interaction" | "view" | "skip";
export const MAX_EVENT_QUEUE = 10;
export interface TrackInfo {
  eventType: EventTypes;
  eventName: string;
  eventData: Record<string, any>;
  featureProvider?: FeatureProviders;
  componentType?: ComponentTypes;
}
export interface HeliumEventInfo extends TrackInfo {
  timestamp: string;
}
type HeliumEvent = HeliumEventGlobalInfo & HeliumEventInfo;
const trackingUrl = "https://function-bun-production-89f8.up.railway.app";
const eventEndpoint = `${trackingUrl}/api/save-event/batch`;
const preferencesEndpoint = `${trackingUrl}/api/save-preferences`;
let eventQueue: HeliumEventInfo[] = [];
let flushTimer = null;
function extractElementInfo(element: HTMLElement) {
  const eventName = element.dataset["hEventName"] as string,
    eventType = element.dataset.hEventType as HeliumEventInfo["eventType"],
    featureProvider = element.dataset
      .hFeatureProvider as HeliumEventInfo["featureProvider"],
    componentType = element.dataset
      .hComponentType as HeliumEventInfo["componentType"],
    eventData = JSON.parse(element.dataset.hEventData ?? "{}");
  return { eventName, eventType, featureProvider, componentType, eventData };
}

export function InitEventTrackingListeners() {
  window.heliumEventQueue = eventQueue;
  handlePageView(window.location.pathname);
  window.addEventListener("click", (e) => {
    const clickElement = e.target as HTMLElement;
    const clickElementHasDataAttribute =
      clickElement.hasAttribute("data-h-event-name");

    const element = clickElementHasDataAttribute
      ? clickElement
      : (clickElement.closest("[data-h-event-name]") as HTMLElement | null);
    if (!element) return;
    const { eventName, eventType, featureProvider, componentType, eventData } =
      extractElementInfo(element);
    if (eventName && eventType) {
      handleEvent({
        timestamp: new Date().toISOString(),
        eventType,
        eventName,
        eventData,
        componentType,
        featureProvider,
      });
    }
  });
  try {
    addEcommTracking();
    overrideDataLayerPushAndQueue({
      debug: true,
      eventsToTrack: window.dataLayerToTrack || [],
    });
  } catch (e) {
    console.error("error in tracking", e, "helium");
  }
  // TODO add Ecommeerce event tracking (add to cart and begin checkout)
}

type pageType = "product" | "collection" | "cart" | "other";

function pageInfo(path: string) {
  let pageType: pageType = "other";
  let handle = "";
  let productHandle = "";
  let collectionHandle = "";
  if (path.includes("product")) {
    pageType = "product";
    handle = path.split("/").at(-1) as string;
    productHandle = handle;
  } else if (path.includes("collection")) {
    pageType = "collection";
    handle = path.split("/").at(-1) as string;
    collectionHandle = handle;
  } else if (path.includes("cart")) {
    pageType = "cart";
  }
  return { pageType, handle, productHandle, collectionHandle };
}
export function handleEcomEvent({
  eventData,
  eventName,
  eventType,
}: HeliumEventInfo) {
  const { pageType, handle } = pageInfo(window.location.pathname);
  eventQueue.push({
    timestamp: new Date().toISOString(),
    eventType,
    eventName,
    eventData: {
      pageType,
      pageHandle: handle,
      ...eventData,
    },
    featureProvider: "native",
  });
}

export function handlePageView(path: string) {
  const { pageType, handle, productHandle, collectionHandle } = pageInfo(path);
  handleEvent({
    timestamp: new Date().toISOString(),
    eventType: "page",
    eventName: pageType,
    eventData: handle
      ? {
          pageType,
          productHandle,
          collectionHandle,
        }
      : {
          pageType,
        },
    featureProvider: "native",
  });
}
export function handleEvent({
  eventData,
  eventName,
  eventType,
  componentType,
  featureProvider,
}: HeliumEventInfo) {
  const { pageType, productHandle, collectionHandle } = pageInfo(
    window.location.pathname,
  );
  eventQueue.push({
    timestamp: new Date().toISOString(),
    eventType,
    eventName,
    componentType,
    featureProvider,
    eventData: {
      pageType,
      productHandle,
      collectionHandle,
      ...eventData,
    },
  });
  if (eventQueue.length > MAX_EVENT_QUEUE) {
    flushEventQueue();
  }
}
function getGlobalInfo() {
  return {
    userId: window.helium.userToken,
    sessionId: window.helium.sessionToken,
    brand: window.helium.brandData.storeId,
    pagePath: window.location.pathname,
  };
}

export function flushEventQueue() {
  const globalInfo = getGlobalInfo();
  if (!eventQueue.length) return;
  const batch = eventQueue.slice().map((event) => {
    return Object.assign({}, globalInfo, event);
  }) as HeliumEvent[];
  eventQueue = [];
  try {
    fetch(eventEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
  } catch (err) {
    eventQueue = batch.concat(eventQueue);
  }
}

export function savePreferences(preferences: Record<string, any>) {
  try {
    fetch(preferencesEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ preferences }),
      keepalive: true,
    });
  } catch (err) {
    console.error(err);
  }
}

const INTERSECTION_THRESHOLD = 0.7; // 70%
const VISIBILITY_DURATION_MS = 2000; // 2 seconds

const elementTimerMap = new WeakMap<Element, NodeJS.Timeout>();
const intersectionObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      const element = entry.target as HTMLElement;

      if (entry.intersectionRatio >= INTERSECTION_THRESHOLD) {
        if (!elementTimerMap.has(element)) {
          const timerId = setTimeout(() => {
            if (
              entry.isIntersecting &&
              entry.intersectionRatio >= INTERSECTION_THRESHOLD
            ) {
              const {
                eventName,
                eventType,
                featureProvider,
                componentType,
                eventData,
              } = extractElementInfo(element); // Extract fresh info

              handleEvent({
                timestamp: new Date().toISOString(),
                eventType,
                eventName,
                componentType,
                featureProvider,
                eventData,
              });
            }
            elementTimerMap.delete(element);
          }, VISIBILITY_DURATION_MS);

          elementTimerMap.set(element, timerId);
          console.log(`Element entered >= 70% view. Starting timer:`, element);
        }
      } else {
        if (elementTimerMap.has(element)) {
          console.log(`Element left >= 70% view. Clearing timer:`, element);
          clearTimeout(elementTimerMap.get(element)!);
          elementTimerMap.delete(element);
        }
      }
    });
  },
  {
    threshold: INTERSECTION_THRESHOLD,
    // rootMargin: '0px 0px -50px 0px'
  },
);

export function addViewListener(
  element: HTMLElement,
  trackingInfo: {
    componentType: ComponentTypes;
    eventData: Record<string, any>;
  },
) {
  element.setAttribute("data-h-component-type", trackingInfo.componentType);
  element.setAttribute("data-h-event-name", "view");
  element.setAttribute("data-h-event-type", "view");
  element.setAttribute(
    "data-h-event-data",
    JSON.stringify(trackingInfo.eventData),
  );
  intersectionObserver.observe(element);
}

export function addEcommTracking(options = {}) {
  // Default options
  const config = {
    debug: false,
    ...options,
  };

  // 1. --- Non-Destructive Initialization ---
  // Check if tracking has already been initialized
  if (window.ecommTrackingInitialized) {
    if (config.debug) {
      console.warn("E-commerce tracking already initialized. Skipping setup.");
    }
    return;
  }

  // Ensure eventQueue is provided and is an array
  if (!Array.isArray(eventQueue)) {
    console.error(
      "addEcommTracking requires an eventQueue array as the first argument.",
    );
    return;
  }

  // Flag that initialization is happening/done
  window.ecommTrackingInitialized = true;
  if (config.debug) {
    console.log("Initializing E-commerce tracking...");
  }

  // --- Helper function to push events ---
  const pushTrackingEvent = (eventName, eventData = {}) => {
    try {
      eventQueue.push({
        eventName: eventName,
        eventType: "ecommerce",
        timestamp: new Date().toISOString(),
        featureProvider: "native", // Or adjust as needed
        eventData: eventData,
      });
      if (config.debug) {
        console.log(`[Tracking] Pushed event: ${eventName}`, eventData);
      }
    } catch (e) {
      console.error(`[Tracking] Error pushing event "${eventName}":`, e);
    }
  };

  // 2. --- Wrap Fetch API ---
  if (typeof window.fetch === "function") {
    const originalFetch = window.fetch; // Keep a reference to the original fetch

    window.fetch = async (...args) => {
      // Use async for easier body handling
      const [resource, fetchOptions] = args;
      let urlString = "";

      // Determine the URL string
      if (typeof resource === "string") {
        urlString = resource;
      } else if (resource instanceof Request) {
        urlString = resource.url;
      } else if (resource && typeof resource.toString === "function") {
        // Handle URL objects or other stringifiable types
        urlString = resource.toString();
      }

      // Check if it's an add to cart request
      const isAddToCartUrl = urlString.includes("/cart/add"); // More specific check if possible (e.g., '/cart/add.js')
      const isChangeQuantityUrl = urlString.includes("/cart/change");

      // Execute the original fetch first
      const responsePromise = originalFetch(...args);

      // Perform tracking after initiating the request (or after it completes)
      if (
        (isAddToCartUrl || isChangeQuantityUrl) &&
        fetchOptions?.method?.toUpperCase() !== "GET"
      ) {
        // Often POST/PATCH/PUT
        if (config.debug) {
          console.log(
            "[Tracking] Intercepted fetch for add_to_cart:",
            urlString,
            fetchOptions,
          );
        }
        const keysToSaveSet = new Set(["id", "quantity", "product-id"]);

        // Attempt to extract relevant data (handle different body types)
        let eventPayload = { url: urlString };
        if (fetchOptions?.body) {
          // --- Addressing the TODO ---
          // This part is tricky and depends heavily on how your site sends data.
          // You might need to inspect network requests to see the format.
          try {
            if (fetchOptions.body instanceof FormData) {
              // Convert FormData to a plain object (may not capture all nuances)
              const formDataObj = {};
              for (const [key, value] of fetchOptions.body.entries()) {
                if (keysToSaveSet.has(key)) {
                  formDataObj[key] = value;
                }
              }
              eventPayload.data = formDataObj;
              eventPayload.dataType = "FormData";
            } else if (typeof fetchOptions.body === "string") {
              try {
                // Attempt to parse as JSON
                eventPayload.data = JSON.parse(fetchOptions.body);
                eventPayload.dataType = "JSONString";
              } catch (jsonError) {
                // If not JSON, keep the raw string (or decide how to handle)
                eventPayload.data = fetchOptions.body;
                eventPayload.dataType = "String";
                if (config.debug)
                  console.log("[Tracking] Fetch body is a non-JSON string.");
              }
            } else if (fetchOptions.body instanceof URLSearchParams) {
              eventPayload.data = Object.fromEntries(
                fetchOptions.body.entries(),
              );
              eventPayload.dataType = "URLSearchParams";
            } else if (
              fetchOptions.body instanceof Blob ||
              fetchOptions.body instanceof ArrayBuffer
            ) {
              eventPayload.dataType = fetchOptions.body.constructor.name;
              // Reading Blobs/ArrayBuffers might require async operations
              // and might be too complex/slow for simple tracking. Decide if needed.
              if (config.debug)
                console.log(
                  "[Tracking] Fetch body is a Blob or ArrayBuffer, data not extracted.",
                );
            } else {
              // Handle other potential body types if necessary
              eventPayload.data = fetchOptions.body; // Store as is
              eventPayload.dataType = "Unknown";
              if (config.debug)
                console.log(
                  "[Tracking] Fetch body is of an unknown type:",
                  typeof fetchOptions.body,
                );
            }
          } catch (bodyError) {
            console.error(
              "[Tracking] Error processing fetch body for tracking:",
              bodyError,
            );
            eventPayload.errorProcessingBody = bodyError.message;
          }
        }

        if (isAddToCartUrl) {
          pushTrackingEvent("add_to_cart", eventPayload);
        } else if (isChangeQuantityUrl) {
          pushTrackingEvent("change_quantity", eventPayload);
        }
      }

      // Return the promise from the original fetch call
      return responsePromise;
    };
    if (config.debug) {
      console.log("[Tracking] Fetch API wrapped.");
    }
  } else {
    if (config.debug) {
      console.warn(
        "[Tracking] window.fetch not found. Cannot wrap for tracking.",
      );
    }
  }

  // 3. --- Wrap onBuyNow Function ---
  if (typeof window.onBuyNow === "function") {
    const originalOnBuyNow = window.onBuyNow;

    window.onBuyNow = (...args) => {
      if (config.debug) {
        console.log("[Tracking] onBuyNow called with:", args);
      }
      // Push tracking events first
      pushTrackingEvent("add_to_cart", { trigger: "onBuyNow" }); // Add context
      pushTrackingEvent("begin_checkout", { trigger: "onBuyNow" });

      // Then, safely call the original function
      try {
        return originalOnBuyNow.apply(window, args); // Use apply to pass arguments correctly
      } catch (e) {
        console.error("[Tracking] Error executing original onBuyNow:", e);
        // Decide if you need to re-throw the error or just log it
      }
    };
    if (config.debug) {
      console.log("[Tracking] window.onBuyNow wrapped.");
    }
  } else {
    if (config.debug) {
      console.warn(
        "[Tracking] window.onBuyNow not found. Cannot wrap for tracking.",
      );
    }
  }

  // 4. --- Wrap onCheckoutClick Function ---
  if (typeof window.onCheckoutClick === "function") {
    const originalOnCheckoutClick = window.onCheckoutClick;

    window.onCheckoutClick = (...args) => {
      if (config.debug) {
        console.log("[Tracking] onCheckoutClick called with:", args);
      }
      // Push tracking event first
      pushTrackingEvent("begin_checkout", { trigger: "onCheckoutClick" }); // Add context

      // Then, safely call the original function
      try {
        return originalOnCheckoutClick.apply(window, args); // Use apply
      } catch (e) {
        console.error(
          "[Tracking] Error executing original onCheckoutClick:",
          e,
        );
      }
    };
    if (config.debug) {
      console.log("[Tracking] window.onCheckoutClick wrapped.");
    }
  } else {
    if (config.debug) {
      console.warn(
        "[Tracking] window.onCheckoutClick not found. Cannot wrap for tracking.",
      );
    }
  }

  if (config.debug) {
    console.log("E-commerce tracking initialization complete.");
  }
}

interface OverrideConfig {
  debug?: boolean;
  eventsToTrack?: string[];
  callOriginalPush?: boolean;
  eventType?: string;
  featureProvider?: string;
}
const eventMapping = {};

function overrideDataLayerPushAndQueue(config: OverrideConfig = {}) {
  if (!Array.isArray(eventQueue)) {
    console.error("[Tracking] Invalid eventQueue provided. Must be an array.");
    return;
  }

  const defaults = {
    debug: false,
    eventsToTrack: ["checkout_initiated", "begin_checkout"], // ["add_to_cart", "begin_checkout"],
    callOriginalPush: true,
    eventType: "ecommerce",
    featureProvider: "native",
  };
  const settings = { ...defaults, ...config };

  window.dataLayer = window.dataLayer || [];

  if (
    typeof window.dataLayer.push !== "function" ||
    window.dataLayer.push._isCustomQueueTracker
  ) {
    if (settings.debug) {
      if (window.dataLayer.push._isCustomQueueTracker) {
        console.warn(
          "[Tracking] window.dataLayer.push already overridden by this queuing script. Skipping.",
        );
      } else {
        console.error(
          "[Tracking] window.dataLayer.push is not a function or dataLayer is malformed.",
        );
      }
    }
    return;
  }

  const originalPush = window.dataLayer.push;

  const pushToTrackingQueue = (
    eventName: string,
    eventData: HeliumEvent["eventData"],
  ) => {
    try {
      const queueItem = {
        eventName: eventMapping[eventName] ?? eventName,
        eventType: settings.eventType as EventTypes,
        timestamp: new Date().toISOString(),
        featureProvider: settings.featureProvider as FeatureProviders,
        eventData: eventData,
      };
      console.log({ queueItem });
      eventQueue.push(queueItem);

      if (settings.debug) {
      }
    } catch (e) {}
  };

  window.dataLayer.push = function (...args) {
    let processed = false;
    let interceptedEventName = null; // To use in logging if original push is skipped

    try {
      let eventName = null;
      let eventPayload = {};

      if (
        args[0].length >= 2 &&
        args[0][0] === "event" &&
        typeof args[0][1] === "string"
      ) {
        eventName = args[0][1];
        eventPayload = args[0][2]?.items ? { items: args[0][2]?.items } : {}; // Payload is the third argument
      } else if (
        args.length === 1 &&
        typeof args[0] === "object" &&
        args[0] !== null &&
        typeof args[0].event === "string"
      ) {
        eventName = args[0].event;
        eventPayload = { items: args[0]?.eventData?.ecommerce?.items }; // Payload is the whole object
      }
      if (eventName && settings.eventsToTrack.includes(eventName)) {
        interceptedEventName = eventName; // Store for potential logging later
        if (settings.debug) {
        }
        pushToTrackingQueue(eventName, eventPayload as Record<string, any>);
        processed = true;
      }
    } catch (error) {}

    let originalPushResult;
    if (settings.callOriginalPush) {
      try {
        originalPushResult = originalPush.apply(window.dataLayer, args);
      } catch (e) {}
    } else if (processed && settings.debug) {
      // Log only if we intercepted AND we are configured *not* to call the original
    }

    return originalPushResult; // Return whatever the original push would have returned
  };

  window.dataLayer.push._isCustomQueueTracker = true;
}
